import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/PeopleTwoTone';
import BusinessIcon from '@mui/icons-material/BusinessTwoTone';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import ActivityList from '../../components/ActivityList';
import PaperSection from '../../components/PaperSection';

interface DashboardStatistics {
  totalUsers: number;
  activeUsers: number;
  totalOrganisations: number;
  activeOrganisations: number;
}

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  to?: string;
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  loading,
  to,
}: StatCardProps & { loading?: boolean }) {
  const navigate = useNavigate();

  return (
    <Card
      sx={{
        height: '100%',
        cursor: to ? 'pointer' : 'default',
        transition: 'transform 0.2s',
        '&:hover': to
          ? {
              borderColor: 'primary.main',
            }
          : {},
      }}
      onClick={() => to && navigate(to)}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {value.toLocaleString()}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {subtitle}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Box
            sx={{
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all statistics in parallel
      const [usersRes, orgsRes] = await Promise.all([
        apiClient.get('/admin/users?limit=1'),
        apiClient.get('/admin/organisations?limit=1'),
      ]);

      if (!usersRes.ok || !orgsRes.ok) {
        throw new Error('Failed to fetch statistics');
      }

      const [usersData, orgsData] = await Promise.all([
        usersRes.json(),
        orgsRes.json(),
      ]);

      setStats({
        totalUsers: usersData.total || 0,
        activeUsers: usersData.total || 0,
        totalOrganisations: orgsData.total || 0,
        activeOrganisations: orgsData.total || 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Overview of users and organisations
        </Typography>
      </Box>

      <Stack gap={4}>
        {error && (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={6}>
            <StatCard
              title="Total Users"
              value={stats?.totalUsers || 0}
              subtitle={stats ? `${stats.activeUsers} active` : ''}
              icon={<PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
              loading={loading}
              to="/app/users"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <StatCard
              title="Organisations"
              value={stats?.totalOrganisations || 0}
              subtitle={stats ? `${stats.activeOrganisations} active` : ''}
              icon={<BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
              loading={loading}
              to="/app/organisations"
            />
          </Grid>
        </Grid>

        <PaperSection title="Recent Activity">
          <ActivityList
            endpoint="/admin/activities"
            showUsers={true}
            showOrganisations={true}
          />
        </PaperSection>
      </Stack>
    </Container>
  );
}
